import type {
  AssetManifest,
  GachaBannerResponse,
  GachaHistoryResponse,
  GachaResultWire,
} from '@kenocyber/shared';
import { GACHA_PITY_THRESHOLD, GACHA_PULL_SIZES } from '@kenocyber/shared';
import { useCallback, useEffect, useState } from 'react';
import {
  fetchGachaBanner,
  fetchGachaHistory,
  HuntApiError,
  newIdempotencyKey,
  pullGacha,
} from '../api/hunt';
import { staticAssetUrl } from '../game/assets';
import { formatDecimal } from './format';
import { GachaReveal } from './GachaReveal';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * The gacha, as a floating panel over the arena.
 *
 * ## Nothing here decides anything
 *
 * Every number on this screen came from `GET /api/gacha/banner`: the prices,
 * the ten chances, the pool, the pity threshold and the counter. The panel
 * does not compute a percentage, does not know the odds table and cannot pick
 * a rarity. A pull posts a banner, a currency, a size and a key, and draws
 * whatever came back.
 *
 * ## The animation runs AFTER the answer
 *
 * The reveal is theatre over a result that is already persisted. The colour
 * that "anticipates" a rarity is chosen once the server has spoken, so nothing
 * on screen can hint at an outcome before it exists - and closing, skipping or
 * reloading changes nothing, because the items were in the vault before the
 * first particle moved.
 *
 * ## No Three.js, at any size
 *
 * Pets are 3D in the arena and PNG everywhere else. Ten reveals must never
 * become ten WebGL contexts; the portal is CSS.
 */

type TabKey = 'pull' | 'odds' | 'history';

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'pull', label: 'GIRAR' },
  { key: 'odds', label: 'CHANCES' },
  { key: 'history', label: 'HISTÓRICO' },
];

interface GachaPanelProps {
  readonly manifest: AssetManifest;
  readonly onClose: () => void;
  /** Told after a pull settles, so the vault can be re-read. */
  readonly onSettled: () => void;
}

export function GachaPanel({ manifest, onClose, onSettled }: GachaPanelProps) {
  const [tab, setTab] = useState<TabKey>('pull');
  const [banner, setBanner] = useState<GachaBannerResponse | null>(null);
  const [history, setHistory] = useState<GachaHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [currency, setCurrency] = useState<'gold' | 'cash'>('gold');
  /** The pull awaiting a confirmation click. Never charged until confirmed. */
  const [confirming, setConfirming] = useState<1 | 10 | null>(null);

  /**
   * The results being revealed.
   *
   * The panel holds WHAT to show; `GachaReveal` owns the timing, the phases
   * and every timer, so a skip or a close cannot leave one running here.
   */
  const [reveal, setReveal] = useState<readonly GachaResultWire[] | null>(null);

  const report = useCallback((unknownError: unknown) => {
    setError(
      unknownError instanceof HuntApiError
        ? (unknownError.reason ?? 'Falha na operação')
        : 'Falha na operação',
    );
  }, []);

  const refreshBanner = useCallback(async () => {
    try {
      setBanner(await fetchGachaBanner());
    } catch (unknownError) {
      report(unknownError);
    }
  }, [report]);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await fetchGachaHistory());
    } catch (unknownError) {
      report(unknownError);
    }
  }, [report]);

  useEffect(() => {
    void refreshBanner();
  }, [refreshBanner]);

  useEffect(() => {
    if (tab === 'history') {
      void refreshHistory();
    }
  }, [tab, refreshHistory]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const doPull = async (size: 1 | 10) => {
    if (!banner) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await pullGacha({
        bannerId: banner.bannerId,
        currency,
        size,
        // A fresh key per attempt. A retry of a request that already committed
        // returns the same items rather than charging twice.
        idempotencyKey: newIdempotencyKey('gacha'),
      });
      setReveal(response.results);
      // The counter and the balances moved; re-read rather than predict.
      await refreshBanner();
      onSettled();
    } catch (unknownError) {
      report(unknownError);
      await refreshBanner();
    } finally {
      setBusy(false);
    }
  };

  const priceFor = (size: 1 | 10): string =>
    banner?.prices.find((entry) => entry.size === size)?.[currency] ?? '0';

  const balance = banner ? banner.balances[currency] : '0';
  const canAfford = (size: 1 | 10): boolean => {
    if (!banner) {
      return false;
    }
    return BigInt(balance) >= BigInt(priceFor(size));
  };

  return (
    <aside className="gacha-panel" data-testid="gacha-panel" aria-label="Gacha">
      <header className="market-panel-header">
        <h2>Gacha</h2>
        <button
          type="button"
          className="overview-close"
          data-testid="gacha-close"
          aria-label="Fechar gacha"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {error ? (
        <p className="trade-error" role="alert" data-testid="gacha-error">
          {error === 'insufficient_funds' ? 'Saldo insuficiente.' : error}
        </p>
      ) : null}

      <div className="trade-tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={tab === entry.key ? 'is-active' : ''}
            data-testid={`gacha-tab-${entry.key}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'pull' ? (
        <section className="market-body gacha-body" data-testid="gacha-pull">
          <div className="gacha-banner">
            <span className="gacha-banner-name">NÚCLEO KENOCYBER</span>
            <span className="market-row-sub">
              Naves, canhões e pets · {banner?.bannerId ?? '—'}
            </span>
          </div>

          {/* The counter, in the numbers the guarantee is actually stated in. */}
          <div className="gacha-pity" data-testid="gacha-pity">
            <span className="gacha-pity-label">GARANTIA</span>
            <span className="gacha-pity-count">
              {banner?.pity.counter ?? 0}/{banner?.pity.threshold ?? GACHA_PITY_THRESHOLD}
            </span>
            <span className="gacha-pity-bar" aria-hidden="true">
              <span
                className="gacha-pity-fill"
                style={{
                  width: `${Math.min(100, ((banner?.pity.counter ?? 0) / (banner?.pity.threshold ?? GACHA_PITY_THRESHOLD)) * 100)}%`,
                }}
              />
            </span>
            <span className="market-row-sub">
              No giro {banner?.pity.threshold ?? GACHA_PITY_THRESHOLD} sem Perfect, Anomaly ou
              Prismatic, um deles é garantido — cada um com 1/3 de chance.
            </span>
          </div>

          <div className="gacha-wallet">
            <label>
              Moeda
              <select
                value={currency}
                data-testid="gacha-currency"
                onChange={(event) => setCurrency(event.target.value as 'gold' | 'cash')}
              >
                <option value="gold">Gold</option>
                <option value="cash">Cash</option>
              </select>
            </label>
            <span className="market-card-price" data-testid="gacha-balance">
              {formatDecimal(balance)} {currency === 'gold' ? 'Gold' : 'Cash'}
            </span>
          </div>

          <div className="gacha-actions">
            {GACHA_PULL_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className="neon-button gacha-pull-button"
                data-testid={`gacha-pull-${size}`}
                disabled={busy || !banner || !canAfford(size)}
                onClick={() => setConfirming(size)}
              >
                <span className="gacha-pull-label">GIRAR {size}</span>
                <span className="gacha-pull-price">
                  {formatDecimal(priceFor(size))} {currency === 'gold' ? 'Gold' : 'Cash'}
                </span>
              </button>
            ))}
          </div>
          <p className="trade-note">
            As chances completas estão na aba CHANCES. Nenhum giro é reembolsável.
          </p>
        </section>
      ) : null}

      {tab === 'odds' ? (
        <section className="market-body gacha-body" data-testid="gacha-odds">
          <p className="trade-note">
            Tabela {banner?.oddsVersion ?? '—'}. As chances somam 100% e valem para Gold e Cash.
          </p>
          <ul className="gacha-bands">
            {(banner?.bands ?? []).map((band) => (
              <li key={band.rarity} style={rarityStyle(band.rarity)}>
                <div className="gacha-band-head">
                  <span className="gacha-band-name">{rarityLabel(band.rarity)}</span>
                  <span className="gacha-band-chance" data-testid={`gacha-chance-${band.rarity}`}>
                    {band.chanceLabel}
                  </span>
                  <span className="market-row-sub">
                    {formatDecimal(String(band.points))} / {formatDecimal(String(band.totalPoints))}
                  </span>
                </div>
                <ul className="gacha-band-items">
                  {band.items.map((item) => (
                    <li key={item.definitionId}>
                      <Thumb manifest={manifest} assetKey={item.assetKey} rarity={band.rarity} />
                      <span className="market-row-sub">
                        {item.name}
                        {item.stars ? ` · ${'★'.repeat(item.stars)}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {tab === 'history' ? (
        <section className="market-body gacha-body" data-testid="gacha-history">
          <ul className="market-rows">
            {(history?.entries ?? []).length === 0 ? (
              <li className="trade-empty">Nenhum giro ainda.</li>
            ) : null}
            {(history?.entries ?? []).map((entry) => (
              <li key={entry.eventId} data-testid={`gacha-history-${entry.eventId}`}>
                <span className="market-row-main">
                  <span className="market-row-name">
                    {entry.size}× · {formatDecimal(entry.cost)}{' '}
                    {entry.currency === 'gold' ? 'Gold' : 'Cash'}
                  </span>
                  <span className="market-row-sub">
                    {new Date(entry.pulledAt).toLocaleString('pt-BR')} · garantia {entry.pityBefore}{' '}
                    → {entry.pityAfter}
                    {entry.forcedAny ? ' · GARANTIA ATIVADA' : ''}
                    {/*
                      The table that actually decided this pull. Old rolls stay
                      in the history after the odds change, and a counter that
                      ran to 60 under v1 is only explicable next to the version
                      that had a 60-roll guarantee.
                    */}
                    {' · '}
                    <span data-testid={`gacha-history-odds-${entry.eventId}`}>
                      tabela {entry.oddsVersion}
                    </span>
                  </span>
                </span>
                <span className="gacha-history-items">
                  {entry.results.map((result) => (
                    <Thumb
                      key={result.instanceId}
                      manifest={manifest}
                      assetKey={result.assetKey}
                      rarity={result.rarity}
                    />
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Confirmation before any charge, naming the currency and the cost. */}
      {confirming ? (
        <div className="market-confirm" data-testid="gacha-confirm" role="dialog">
          <p>
            Girar <strong>{confirming}×</strong> por{' '}
            <strong>
              {formatDecimal(priceFor(confirming))} {currency === 'gold' ? 'Gold' : 'Cash'}
            </strong>
            ?
          </p>
          <p className="market-confirm-warning">
            A cobrança é definitiva e o resultado é decidido pelo servidor. Não há reembolso.
          </p>
          <div className="market-confirm-actions">
            <button
              type="button"
              className="ghost-button"
              data-testid="gacha-confirm-cancel"
              onClick={() => setConfirming(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="neon-button"
              data-testid="gacha-confirm-pull"
              disabled={busy}
              onClick={() => {
                const size = confirming;
                setConfirming(null);
                void doPull(size);
              }}
            >
              CONFIRMAR
            </button>
          </div>
        </div>
      ) : null}

      {/* The reveal. Theatre over a result that is already in the vault:
          skipping moves an index, closing unmounts a div, and neither can
          change what the account owns. */}
      {reveal ? (
        <GachaReveal manifest={manifest} results={reveal} onClose={() => setReveal(null)} />
      ) : null}
    </aside>
  );
}

/**
 * One thumbnail, from the manifest.
 *
 * The same PNG the inventory and the marketplace draw, at the same 99% under
 * `object-fit: contain`. A pet's picture is its evolution's art; there is no
 * renderer here.
 */
function Thumb({
  manifest,
  assetKey,
  rarity,
}: {
  manifest: AssetManifest;
  assetKey: string | null;
  rarity: string;
}) {
  const url = assetKey ? staticAssetUrl(manifest, assetKey) : null;
  return (
    <span
      className="gacha-thumb"
      data-rarity={rarity}
      data-paint={rarityPaint(rarity as never)}
      style={rarityStyle(rarity as never)}
    >
      {url ? <img src={url} alt="" draggable={false} loading="lazy" decoding="async" /> : null}
    </span>
  );
}
